import { useEffect, useState } from 'react';
import type { Falha } from '../../acesso';
import { SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { useVeCusto } from '../../design/sigilo';
import { reais } from '../../produtos';
import { lerVencidosDoMes } from '../../validades';
import type { VencidosDoMes } from '../../validades';
import { ErroDaLista, Esqueleto } from '../equipe/Equipe';
import type { Periodo } from '../../paineis';

/**
 * VENCIDOS POR MÊS — o que venceu em cada mês, inclusive o que já saiu do programa na virada.
 *
 * Por padrão abre o mês ANTERIOR ("virou outubro: o que venceu em setembro"). Para cada lote: o
 * que o programa vendeu, o que sobrou e quanto valia a custo. Sobra sem saldo informado e custo
 * desconhecido aparecem como "indisponível" — nunca zero.
 */

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const nomeDoMes = (m: string) => {
  const [a, n] = m.split('-');
  return `${MESES[Number(n) - 1] ?? String(n)} de ${a ?? ''}`;
};
const curto = (m: string) => {
  const [a, n] = m.split('-');
  return `${(MESES[Number(n) - 1] ?? '').slice(0, 3)}/${(a ?? '').slice(2)}`;
};
const somarMes = (m: string, delta: number) => {
  const t = new Date(`${m}-15T12:00:00Z`);
  t.setUTCMonth(t.getUTCMonth() + delta);
  return t.toISOString().slice(0, 7);
};
const diaMes = (d: string) => d.split('-').reverse().slice(0, 2).join('/');
const quando = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });

export function VencidosPorMes({
  periodo = null,
}: {
  /** O filtro de período do módulo (dono, 05/10), pela validade. null = um mês por vez. */
  periodo?: Periodo | null;
} = {}) {
  const veCusto = useVeCusto();
  const hoje = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  const mesAtual = hoje.slice(0, 7);
  const [mes, setMes] = useState(() => somarMes(mesAtual, -1));
  const [tentativa, setTentativa] = useState(0);
  const [dados, setDados] = useState<VencidosDoMes | null>(null);
  const [falha, setFalha] = useState<Falha | null>(null);

  useEffect(() => {
    let vivo = true;
    setFalha(null);
    void lerVencidosDoMes(periodo ?? mes).then((r) => {
      if (!vivo) return;
      if (r.ok) setDados(r.dados);
      else setFalha(r.falha);
    });
    return () => {
      vivo = false;
    };
  }, [mes, tentativa, periodo?.de, periodo?.ate]);

  if (falha?.tipo === 'sem-acesso') return <SemAcesso area="Pré-vencidos" />;
  const doMes = periodo
    ? dados?.de === periodo.de && dados.ate === periodo.ate
      ? dados
      : null
    : dados?.mes === mes
      ? dados
      : null;
  const r = doMes?.resumo ?? null;
  const esteMes = mes === mesAtual;

  return (
    <div className="ac pv-venc">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo">
            {periodo
              ? `Vencimentos de ${diaMes(periodo.de)} a ${diaMes(periodo.ate)}`
              : `Vencidos em ${nomeDoMes(mes)}`}
          </h1>
          <div className="ac-subtitulo">
            {periodo
              ? 'Lotes com validade neste período — os que já saíram do programa também.'
              : esteMes
                ? 'Mês atual: os lotes ainda valem até o último dia do mês e saem sozinhos na virada.'
                : 'Lotes que venceram neste mês. Saíram do programa sozinhos na virada e ficam registrados aqui.'}
          </div>
        </div>
        <div className="ac-acoes pv-mes" style={periodo ? { display: 'none' } : undefined}>
          <button
            type="button"
            className="ac-botao"
            aria-label="Mês anterior"
            onClick={() => setMes(somarMes(mes, -1))}
          >
            {/* o CSS (.pv-mes .ac-botao:first-child svg) vira esta seta para a esquerda */}
            <Icone nome="chev-r" tamanho={14} traco={1.8} />
          </button>
          <span className="pv-mes-nome">{nomeDoMes(mes)}</span>
          <button
            type="button"
            className="ac-botao"
            aria-label="Próximo mês"
            disabled={esteMes}
            onClick={() => setMes(somarMes(mes, 1))}
          >
            <Icone nome="chev-r" tamanho={14} traco={1.8} />
          </button>
        </div>
      </div>

      {!periodo && dados && dados.meses.length > 0 && (
        <div className="ac-filtros">
          {dados.meses.slice(0, 12).map((m) => (
            <button
              key={m.mes}
              type="button"
              className={`ac-chip${m.mes === mes ? ' ac-chip-ativo' : ''}`}
              aria-pressed={m.mes === mes}
              onClick={() => setMes(m.mes)}
            >
              {curto(m.mes)} · {m.lotes}
            </button>
          ))}
        </div>
      )}

      <div className="pv-faixas">
        <div className="ac-kpi">
          <div className="ac-kpi-rotulo">LOTES QUE VENCERAM</div>
          <div className="ac-kpi-valor">{r ? r.lotes : '—'}</div>
          <div className="ac-nota">{r ? `${String(r.produtos)} produto(s)` : ''}</div>
        </div>
        <div className="ac-kpi">
          <div className="ac-kpi-rotulo">VENDIDAS PELO PROGRAMA</div>
          <div className="ac-kpi-valor">{r ? r.vendidas : '—'}</div>
          <div className="ac-nota">unidades lançadas no InovaPay</div>
        </div>
        <div className={`ac-kpi${r && r.sobraram > 0 ? ' ac-kpi-alerta' : ''}`}>
          <div className="ac-kpi-rotulo">SOBRARAM</div>
          <div className="ac-kpi-valor">{r ? r.sobraram : '—'}</div>
          <div className="ac-nota">
            {r && r.semSaldoInformado
              ? `${String(r.semSaldoInformado)} lote(s) sem saldo informado`
              : 'unidades não vendidas'}
          </div>
        </div>
        {veCusto && (
          <div className="ac-kpi">
            <div className="ac-kpi-rotulo">SOBRA A CUSTO</div>
            <div className="ac-kpi-valor">
              {r ? (r.valorACusto ? reais(r.valorACusto) : 'indisponível') : '—'}
            </div>
            <div className="ac-nota">
              {r && r.semCusto
                ? `${String(r.semCusto)} lote(s) sem custo do produto`
                : 'saldo × custo do produto'}
            </div>
          </div>
        )}
      </div>

      <div className="ac-cartao pv-venc-lista">
        {!doMes && !falha && <Esqueleto linhas={5} />}
        {falha && (
          <ErroDaLista
            {...(falha.tipo === 'offline' ? { texto: 'Sem conexão. Tente de novo.' } : {})}
            aoTentar={() => setTentativa((t) => t + 1)}
          />
        )}
        {doMes && doMes.lotes.length === 0 && (
          <div className="ac-vazio">
            <div className="ac-vazio-titulo">Nenhum lote venceu em {nomeDoMes(mes)}</div>
            <div className="ac-vazio-texto">Não há lote de pré-vencido com validade neste mês.</div>
          </div>
        )}
        {doMes && doMes.lotes.length > 0 && (
          <div className="ac-tabela-rolo">
            <table className="ac-tabela">
              <thead>
                <tr>
                  <th>VALIDADE</th>
                  <th>PRODUTO · LOTE</th>
                  <th className="ac-direita">VENDIDAS</th>
                  <th className="ac-direita">SOBRARAM</th>
                  {veCusto && <th className="ac-direita">SOBRA A CUSTO</th>}
                  <th>SITUAÇÃO</th>
                </tr>
              </thead>
              <tbody>
                {doMes.lotes.map((l) => (
                  <tr key={l.loteId}>
                    <td className="ac-mono">{diaMes(l.validade)}</td>
                    <td>
                      <div className="ac-pessoa-nome">{l.descricao}</div>
                      <div className="pd-codigos">
                        {[l.codigo ? `lote ${l.codigo}` : 'lote sem código', l.codigoProduto]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </td>
                    <td className="ac-direita ac-num">{l.vendidas}</td>
                    <td className="ac-direita ac-num">{l.sobraram ?? 'indisponível'}</td>
                    {veCusto && (
                      <td className="ac-direita ac-num">
                        {l.valorACusto ? reais(l.valorACusto) : 'indisponível'}
                      </td>
                    )}
                    <td>
                      {l.situacao === 'no_mes'
                        ? 'Ainda no mês da validade'
                        : l.situacao === 'expirado'
                          ? `Saiu sozinho${l.saiuEm ? ` em ${quando(l.saiuEm)}` : ''}`
                          : `Desativado${l.saiuEm ? ` em ${quando(l.saiuEm)}` : ''}${l.motivo ? ` · ${l.motivo}` : ''}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
